import { supabase } from '../lib/supabase'
import { verifyTenancyWriteAccess } from '../lib/auth'
import { createAuditLog } from '../lib/audit'
import { emailSchema } from '../lib/validators'
import { sendInvite } from './notifications/invites'

/** Verify the current user can write to the property linked to a tenant invite */
async function verifyInviteWriteAccess(inviteId: string): Promise<{ skipOnboarding: boolean }> {
  const { data, error } = await supabase
    .from('tenant_invites')
    .select('skip_onboarding, tenancy_id')
    .eq('id', inviteId)
    .single()

  if (error || !data) throw new Error('Invite not found')
  await verifyTenancyWriteAccess(data.tenancy_id)
  return { skipOnboarding: (data.skip_onboarding as boolean) ?? false }
}

export interface TenantInviteDetails {
  id: string
  full_name: string
  email: string
  phone: string | null
  status: string
  landlord_name: string
  landlord_company: string | null
  property_address: string
}

export interface CreateTenantInviteParams {
  tenancyId: string
  email: string
  fullName: string
  phone?: string
  isPrimary?: boolean
  skipOnboarding?: boolean
}

// ============================================================
// CRUD Operations
// ============================================================

export async function createTenantInvite(params: CreateTenantInviteParams): Promise<{ id: string }> {
  const userId = await verifyTenancyWriteAccess(params.tenancyId)

  const { data, error } = await supabase
    .from('tenant_invites')
    .insert({
      tenancy_id: params.tenancyId,
      email: params.email,
      full_name: params.fullName,
      phone: params.phone || null,
      is_primary: params.isPrimary || false,
      skip_onboarding: params.skipOnboarding || false,
      invited_by: userId
    })
    .select('id')
    .single()

  if (error) throw error

  // Audit log: tenant invite created (legally critical for onboarding flow)
  await createAuditLog({
    action: 'tenant_invite_created',
    entityType: 'tenant_invite',
    entityId: data.id,
    metadata: {
      tenancy_id: params.tenancyId,
      tenant_email: params.email,
      tenant_name: params.fullName,
      is_primary: params.isPrimary || false,
      skip_onboarding: params.skipOnboarding || false
    }
  })

  return { id: data.id }
}

export async function getTenantInviteDetails(inviteId: string): Promise<TenantInviteDetails | null> {
  const { data, error } = await supabase.rpc('get_tenant_invite_details', { invite_id: inviteId })
  if (error || !data) return null
  return data as TenantInviteDetails
}

export async function activateTenantInvite(inviteId: string, userId: string): Promise<{ tenancyId: string }> {
  const { data, error } = await supabase.rpc('activate_tenant_invite', {
    invite_id: inviteId,
    invite_user_id: userId
  })

  if (error) throw error

  // Audit log: tenant joined tenancy (legally critical for onboarding flow)
  await createAuditLog({
    action: 'tenant_invite_accepted',
    entityType: 'tenant_invite',
    entityId: inviteId,
    metadata: {
      tenancy_id: data.tenancy_id,
      user_id: userId
    }
  })

  return { tenancyId: data.tenancy_id }
}

export interface PendingInviteSummary {
  tenancy_id: string
  full_name: string
  email: string
  phone: string | null
}

/** Fetch all pending invites for the current user's tenancies in one query.
 *  RLS on tenant_invites already restricts to the landlord's own tenancies,
 *  so no explicit owner_id filter is needed here. */
export async function getAllPendingInvites(): Promise<PendingInviteSummary[]> {
  const { data, error } = await supabase
    .from('tenant_invites')
    .select('tenancy_id, full_name, email, phone')
    .eq('status', 'pending')

  if (error) throw error
  return (data || []).map(d => ({
    tenancy_id: d.tenancy_id,
    full_name: d.full_name,
    email: d.email,
    phone: d.phone,
  }))
}

export async function getPendingInvitesForTenancy(tenancyId: string): Promise<TenantInviteDetails[]> {
  const { data, error } = await supabase
    .from('tenant_invites')
    .select('id, full_name, email, phone, status, created_at')
    .eq('tenancy_id', tenancyId)
    .eq('status', 'pending')
    .order('created_at', { ascending: false })

  if (error) throw error
  return data as TenantInviteDetails[]
}

// ============================================================
// LocalStorage helpers for signup flow
// ============================================================

const STORAGE_KEY = 'lentals_tenant_invite'

export interface StoredTenantInvite {
  id: string
  full_name: string
  email: string
  phone: string | null
  landlord_name: string
  landlord_company: string | null
  property_address: string
}

export function storeTenantInviteData(data: StoredTenantInvite): void {
  const wrapped = { data, expiresAt: Date.now() + 60 * 60 * 1000 }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(wrapped))
}

export function getStoredTenantInviteData(): StoredTenantInvite | null {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) return null
  try {
    const parsed = JSON.parse(stored)
    // Support TTL-wrapped format
    if (parsed.expiresAt) {
      if (Date.now() > parsed.expiresAt) {
        localStorage.removeItem(STORAGE_KEY)
        return null
      }
      return parsed.data
    }
    // Legacy format (no TTL) — clear it
    localStorage.removeItem(STORAGE_KEY)
    return null
  } catch {
    return null
  }
}

export function clearStoredTenantInviteData(): void {
  localStorage.removeItem(STORAGE_KEY)
}

// ============================================================
// Invite URL & Email
// ============================================================

export function getTenantInviteUrl(inviteId: string): string {
  return `${window.location.origin}/tenant-invite/${inviteId}`
}

export interface SendTenantInviteEmailParams {
  inviteId: string
  recipientEmail: string
  recipientName: string
  propertyAddress: string
  landlordName: string
}

export async function sendTenantInviteEmail(
  params: SendTenantInviteEmailParams
): Promise<{ success: boolean; error?: string }> {
  try {
    await verifyInviteWriteAccess(params.inviteId)

    // Validate email
    const emailValidation = emailSchema.safeParse(params.recipientEmail)
    if (!emailValidation.success) {
      return { success: false, error: 'Invalid email address' }
    }

    // The server reads the address, the name, the property, `skip_onboarding`
    // and the magic link off the tenant_invites row (PR 2). The validation
    // above stays so a typo in the form is reported here rather than silently
    // not sent, but the address on the row is what gets mailed.
    const result = await sendInvite('tenant_invite', params.inviteId)

    if (!result.success) {
      return { success: false, error: result.error || 'Failed to send email. Please try again.' }
    }

    // Audit log
    await createAuditLog({
      action: 'tenant_invite_sent',
      entityType: 'tenant_invite',
      entityId: params.inviteId,
      metadata: {
        recipient_email: params.recipientEmail,
        recipient_name: params.recipientName,
        property_address: params.propertyAddress
      }
    })

    return { success: true }
  } catch (error) {
    console.error('Error sending tenant invite email:', error)
    return { success: false, error: 'An unexpected error occurred' }
  }
}

// ============================================================
// Onboarding Reminder
// ============================================================

export async function sendOnboardingReminder(
  tenancyId: string,
  tenantUserId: string,
  outstandingSteps: string[]
): Promise<{ success: boolean; error?: string }> {
  try {
    // The gate is inside the RPC (get_my_property_ids, migration 398), which
    // also checks the recipient is an active tenant on THIS tenancy and
    // composes the message from the property row. This call keeps
    // verifyTenancyWriteAccess so the UI fails fast with its own message, but
    // it is no longer what makes the send safe.
    await verifyTenancyWriteAccess(tenancyId)

    const { error } = await supabase.rpc('send_onboarding_reminder', {
      p_tenancy_id: tenancyId,
      p_tenant_user_id: tenantUserId,
      p_outstanding_steps: outstandingSteps,
    })

    if (error) {
      return { success: false, error: error.message || 'Failed to send reminder' }
    }

    await createAuditLog({
      action: 'onboarding_reminder_sent',
      entityType: 'tenancy',
      entityId: tenancyId,
      metadata: {
        tenant_user_id: tenantUserId,
        outstanding_steps: outstandingSteps,
      }
    })

    return { success: true }
  } catch (error) {
    console.error('Error sending onboarding reminder:', error)
    return { success: false, error: 'An unexpected error occurred' }
  }
}
